import React, { useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";

import {
  List, AutoSizer, CellMeasurer, CellMeasurerCache
} from "react-virtualized";

import { Response, ResponseFilterItem } from "resourceModels";
import { ResponseRecord } from "reducers/responses/reducer";
import { fetchMany } from "actions/resources";
import { getResources } from "selectors/resources";
import ResponseListItem from "components/Common/ResponseListItem";
import InputSearch from "components/Common/InputSearch";
import SvgIcon from "components/Common/SvgIcon";
import Button from "components/Common/Button";
import classNames from "classnames";
import ResponseItem from "./ResponseItem";

import "./style.scss";

const CACHE_DEFAULT_HEIGHT = 85;
const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: CACHE_DEFAULT_HEIGHT,
  minHeight: 56
});

const BLOCK_NAME = "ResponseSelector";

class ResponseSelector extends React.PureComponent {
  fetchResponseFilterItemsDebounced = debounce(this.fetchResponseFilterItems, 200);

  /**
   * @param {Object} props
   */
  constructor(props) {
    const { responses } = props;

    super(props);
    this.selectorRef = React.createRef();
    this.dropdownRef = React.createRef();
    this.resultsRef = React.createRef();

    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleKeyPressOutside = this.handleKeyPressOutside.bind(this);
    this.handleSearchClear = this.handleSearchClear.bind(this);
    this.handleAnswerClear = this.handleAnswerClear.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleOnFocus = this.handleOnFocus.bind(this);
    this.newSelected = this.newSelected.bind(this);
    this.responseWasSelected = this.responseWasSelected.bind(this);
    this.adjustSelectorPositionOnScroll = this.adjustSelectorPositionOnScroll.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
    this.throttledAdjustSelectorPositionOnScroll = throttle(
      this.adjustSelectorPositionOnScroll,
      15
    );
    this.filteredResponseTimer = null;

    this.defaultPosition = {
      left: 0,
      top: 0
    };

    this.state = {
      selected: null,
      activeIndex: 0,
      resultsVisible: false,
      bottomAligned: false,
      position: this.defaultPosition,
      filteredResponses: responses
    };
  }

  /**
   * Lifecycle Methods
   */

  /**
   * componentDidMount
   */
  componentDidMount() {
    const { fixed } = this.props;

    this.setPreselect();

    if (this.selectorRef.current) {
      if (fixed) {
        this.setState({
          bottomAligned: this.selectorRef.current.getBoundingClientRect().top >= 400

        });
      }

      const position = this.getSelectorPosition();
      this.setState({
        position
      });
    }

    document.addEventListener("scroll", this.throttledAdjustSelectorPositionOnScroll, true);
  }

  /**
   * @param {Object} prevProps
   * @param {Object} prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      searchQuery,
      preSelect,
      responseFilterItems,
      filteredResponses,
      responses
    } = this.props;
    const { resultsVisible } = this.state;

    if (prevProps.preSelect !== preSelect) {
      this.setPreselect();
    }

    if (prevState.resultsVisible !== resultsVisible) {
      if (resultsVisible) {
        document.addEventListener("click", this.handleClickOutside, true);
        document.addEventListener("keydown", this.handleKeyPressOutside, true);
      } else {
        document.removeEventListener("click", this.handleClickOutside, true);
        document.removeEventListener("keydown", this.handleKeyPressOutside, true);
      }
    }

    if (searchQuery) {
      // Fetch response filter items if needed
      if (
        searchQuery !== prevProps.searchQuery &&
        !responseFilterItems
      ) {
        this.fetchResponseFilterItemsDebounced(searchQuery);
      }

      // Update filtered responses if there are new ones
      if (filteredResponses && filteredResponses !== prevProps.filteredResponses) {
        this.filteredResponseTimer = setTimeout(() => this.setState({ filteredResponses }), 0);
      }
    } else {
      // If search query is empty, show all responses
      this.filteredResponseTimer = setTimeout(
        () => this.setState({ filteredResponses: responses }),
        0
      );
    }

    this.clearCellMeasurementCache();
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    document.removeEventListener("scroll", this.throttledAdjustSelectorPositionOnScroll, true);
    clearTimeout(this.filteredResponseTimer);
  }

  /**
   * Methods
   */

  /**
   * If a preSelected answer is passed in, filters answer list based on it
   */
  setPreselect() {
    const { preSelect, updateSearchQuery } = this.props;

    if (preSelect) {
      this.setState({
        selected: preSelect
      });
    }

    updateSearchQuery("");
  }

  /**
   * Calculates dropdown position based on the button's location in the DOM
   *
   * @returns {Object}
   */
  getSelectorPosition() {
    const {
      inModal,
      // eslint-disable-next-line react/prop-types
      introContentRef
    } = this.props;
    const { bottomAligned } = this.state;
    const selectorElement = this.selectorRef.current;
    const selectorElementBounds = selectorElement.getBoundingClientRect();
    const { width } = selectorElementBounds;
    const top = selectorElementBounds.top + selectorElementBounds.height + 4;
    const bottom = window.innerHeight - selectorElementBounds.bottom +
                    selectorElementBounds.height + 4;
    const introElem = introContentRef;

    let position; let left;

    if (inModal) {
      // eslint-disable-next-line prefer-destructuring
      left = selectorElementBounds.left;
    } else {
      left = selectorElementBounds.left + Math.round(window.scrollX);
    }

    if (bottomAligned) {
      if (introElem && introElem.current &&
        (bottom < introElem.current.getBoundingClientRect().top ||
        bottom > introElem.current.getBoundingClientRect().bottom)
      ) {
        this.setState({
          resultsVisible: false
        });
      } else {
        position = {
          bottom,
          left,
          width
        };
      }
    } else if (introElem && introElem.current &&
              (top < introElem.current.getBoundingClientRect().top ||
              top > introElem.current.getBoundingClientRect().bottom)
    ) {
      this.setState({
        resultsVisible: false
      });
    } else {
      position = {
        top,
        left,
        width
      };
    }

    return position;
  }

  /**
   */
  clearCellMeasurementCache() {
    const { filteredResponses } = this.state;
    // Clear cached list cell dimensions and recompute on new props
    // (i.e. initial render, searching, validation)
    filteredResponses.forEach((response, i) => {
      cache.clear(i);
    });
  }

  /**
   * @param {String} searchQuery
   */
  fetchResponseFilterItems(searchQuery) {
    const { fetchResponseFilterItems } = this.props;

    fetchResponseFilterItems(searchQuery);
  }

  /**
   * Closes dropdown on Tab
   *
   * @param {Object} event
   */
  handleKeyPressOutside(event) {
    if (event.keyCode === 9) {
      this.setState({
        resultsVisible: false
      });
      this.resetDropdown();
    }
  }

  /**
   * Closes dropdown when a click outside the dropdown bounds is registered
   *
   * @param {MouseEvent} event
   */
  handleClickOutside(event) {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target)
    ) {
      this.setState({
        resultsVisible: false
      });
      this.resetDropdown();
    }
  }

  /**
   * Recalculates the dropdown's position when content is scrolled
   *
   * @param {ScrollEvent} event
   */
  adjustSelectorPositionOnScroll(event) {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target)
    ) {
      const position = this.getSelectorPosition();
      this.setState({
        position
      });
    }
  }

  /**
   * Handles selecting the "Create New Answer" option in the dropdown
   */
  newSelected() {
    const { onDidUpdate, updateSearchQuery } = this.props;

    this.setState(
      {
        resultsVisible: false,
        selected: null
      },
      () => {
        onDidUpdate(new ResponseRecord({
          id: "__new__"
        }));
      }
    );

    updateSearchQuery("New Answer");
  }

  /**
   * Resets the answer list to a pre-filtered state when the user clears the
   * searchbar
   */
  handleSearchClear() {
    const { updateSearchQuery } = this.props;

    updateSearchQuery("");
  }

  /**
   * Removes the selected answer and resets the answer list to a pre-filtered
   * state when the user clears the selected answer
   */
  handleAnswerClear() {
    const {
      onClear,
      onDidUpdate,
      updateSearchQuery
    } = this.props;

    if (onClear) {
      onClear();
    }

    this.setState(
      {
        selected: null
      },
      () => {
        onDidUpdate(null);
      }
    );

    updateSearchQuery("");
  }

  /**
   * Searches answer list for occurrences of the inputted search query
   *
   * @param {Object} e
   */
  handleOnChange(e) {
    const { updateSearchQuery } = this.props;

    updateSearchQuery(e.target.value);
  }

  /**
   * Renders answer dropdown when container div is focused
   *
   * @param {Object} e
   */
  handleOnFocus(e) {
    const { fixed } = this.props;

    this.setState({
      resultsVisible: true,
      activeIndex: 0
    });

    if (!fixed) {
      this.setState({
        bottomAligned: e.target.getBoundingClientRect().top >= 400
      });
    }

    const position = this.getSelectorPosition();
    this.setState({
      position
    });
  }

  /**
   * Focuses answer dropdown container div on Enter key press
   *
   * @param {Object} e
   */
  handleEnter(e) {
    const keycode = (e.keyCode ? e.keyCode : e.which);

    if (keycode === "13") {
      this.handleOnFocus(e);
    }
  }

  /**
   * Handle keyDown event to leverage arrow keys and enter keypresses
   *
   * @param {Object} event
   */
  handleKeyPress(event) {
    const { activeIndex, filteredResponses } = this.state;
    const numOptions = filteredResponses.size - 1;

    if (event.keyCode === 38 && activeIndex > 0) {
      // Arrow up
      this.handleArrow(true);
    } else if (
      event.keyCode === 40 &&
      activeIndex < numOptions
    ) {
      // Arrow down
      this.handleArrow(false);
    } else if (event.keyCode === 13) {
      this.responseWasSelected(filteredResponses.get(activeIndex));
    } else if (event.keyCode === 27) {
      this.setState({
        resultsVisible: false
      });
    }
  }

  /**
   * Handle setting next active index, as well as scrolling dropdown window if needed
   *
   * @param {Boolean} isUp
   */
  handleArrow(isUp) {
    const direction = isUp ? -1 : 1;
    const { activeIndex } = this.state;

    this.setState(
      prevState => ({
        activeIndex: prevState.activeIndex + direction
      }),
      () => {
        const newActiveElement = this.resultsRef.current.childNodes.item(
          activeIndex
        );

        if (!newActiveElement) {
          return;
        }

        if (
          newActiveElement.offsetTop + newActiveElement.offsetHeight >
          this.resultsRef.current.scrollTop + this.resultsRef.current.offsetHeight
        ) {
          this.resultsRef.current.scrollTop = (
            newActiveElement.offsetTop +
            newActiveElement.offsetHeight -
            this.resultsRef.current.offsetHeight
          );
        } else if (
          this.resultsRef.current.scrollTop > newActiveElement.offsetTop
        ) {
          this.resultsRef.current.scrollTop = newActiveElement.offsetTop;
        }
      }
    );
  }

  /**
   * Resets response selector dropdown and search query to their unfiltered states
   */
  resetDropdown() {
    const { updateSearchQuery } = this.props;

    updateSearchQuery("");
  }

  /**
   * Sets an answer as selected, closing the dropdown and resetting the answer
   * list to the pre-filtered state
   *
   * @param {Immutable.Record|Response} response
   * @returns {Object}
   */
  responseWasSelected(response) {
    const {
      clearAfterUpdate,
      onDidUpdate
    } = this.props;

    const newState = {
      resultsVisible: false,
      activeIndex: 0
    };

    if (clearAfterUpdate) {
      newState.selected = null;
    } else {
      newState.selected = response;
    }

    onDidUpdate(response);
    this.setState(newState);
    this.resetDropdown();
  }

  /**
   * Renders a single answer in the sidebar list
   * Must have this for `react-virtualized`
   *
   * @param {Object} param
   * @param {Object} param.parent the parent element
   * @param {String} param.key Unique key within array of rows
   * @param {Number} param.index Index of row within collection
   * @param {Object} param.style Sizing and position style to be applied to the row
   *
   * @returns {ReactElement}
   */
  rowRenderer({
    parent,
    key,
    index,
    style
  }) {
    const { responseFilterItems } = this.props;
    const { activeIndex, filteredResponses } = this.state;
    const response = filteredResponses.get(index);
    const responseFilterItem = (
      responseFilterItems &&
      responseFilterItems.find(item => item.responseId === response.id)
    );

    return (
      <CellMeasurer
        key={key}
        cache={cache}
        parent={parent}
        rowIndex={index}
        columnIndex={0}
      >
        <ResponseListItem
          index={index}
          style={style}

          key={index}
          selected={null}
          responseId={response.id}
          handleClick={this.responseWasSelected}
          isActive={activeIndex === index}
          responseFilterItem={responseFilterItem}
        />
      </CellMeasurer>
    );
  }

  /**
   * @returns {ReactElement|null}
   */
  renderResults() {
    const {
      resultsVisible,
      bottomAligned,
      filteredResponses
    } = this.state;

    if (!resultsVisible) {
      return null;
    }

    const approxHeight = Math.min(filteredResponses.size, 3) * CACHE_DEFAULT_HEIGHT;

    return (
      <div
        className={classNames(
          `${BLOCK_NAME}__results`,
          {
            [`${BLOCK_NAME}__results--align-bottom`]: bottomAligned
          }
        )}
        ref={this.resultsRef}
      >
        <div style={{ height: approxHeight }}>
          <AutoSizer>
            {({ width }) => (
              <List
                style={{ outline: "none" }}
                width={width}
                height={approxHeight}
                deferredMeasurementCache={cache}
                rowCount={filteredResponses.size}
                rowHeight={cache.rowHeight}
                rowRenderer={this.rowRenderer}
              />
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }

  /**
   * @returns {ReactElement}
   */
  renderInputSearch() {
    const {
      resultsVisible,
      bottomAligned
    } = this.state;
    const {
      placeholder,
      hasCreateNewAnswerButton,
      onCreateNewAnswer,
      searchQuery
    } = this.props;

    return (
      <div
        className={classNames({
          [`${BLOCK_NAME}__input-search-container--active`]: resultsVisible,
          [`${BLOCK_NAME}__input-search-container--top-aligned`]: !bottomAligned,
          [`${BLOCK_NAME}__input-search-container--bottom-aligned`]: bottomAligned
        })}
      >
        <div className={`${BLOCK_NAME}__wrapper`}>
          <InputSearch
            customClassName={`${BLOCK_NAME}__search`}
            onFocus={this.handleOnFocus}
            onChange={this.handleOnChange}
            onClear={this.handleSearchClear}
            onKeyDown={this.handleKeyPress}
            data-drag-target="ignore"
            placeholder={placeholder}
            hideChevron
            autoFocus
          />
          {hasCreateNewAnswerButton && (
            <Button
              onClick={
                onCreateNewAnswer ? () => onCreateNewAnswer(searchQuery) : this.newSelected
              }
              title="Create a new Answer"
              icon="Compose"
              iconLarge
              fillColor="white"
              customClassName={`${BLOCK_NAME}__button`}
            />
          )}
        </div>
      </div>
    );
  }

  /**
   * Renders content by creating a portal in the container specified by portalElementId
   * @param {String} portalElementId
   * @returns {ReactElement}
   */
  renderContent(portalElementId) {
    const { position, bottomAligned } = this.state;

    const content = (
      <div
        className={classNames(
          `${BLOCK_NAME}__dropdown`, {
            [`${BLOCK_NAME}__dropdown--top-aligned`]: !bottomAligned,
            [`${BLOCK_NAME}__dropdown--bottom-aligned`]: bottomAligned
          }
        )}
        ref={this.dropdownRef}
        style={position}
      >
        {bottomAligned ? (
          <>
            {this.renderResults()}
            {this.renderInputSearch()}
          </>
        ) : (
          <>
            {this.renderInputSearch()}
            {this.renderResults()}
          </>
        )}
      </div>
    );

    return (
      <>
        {bottomAligned
          ? ReactDOM.createPortal(
            content,
            document.getElementById(portalElementId)
          )
          : ReactDOM.createPortal(
            content,
            document.getElementById(portalElementId)
          )}
      </>
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      customClassName,
      questionsView,
      invalid,
      tags,
      disabled,
      fixed,
      inModal,
      hasCreateNewAnswerButton,
      searchQuery
    } = this.props;
    const {
      resultsVisible,
      selected,
      bottomAligned
    } = this.state;

    return (
      <div
        className={classNames(
          `${BLOCK_NAME}`,
          {
            [`${BLOCK_NAME}--questions`]: questionsView,
            [customClassName]: customClassName
          }
        )}
        ref={this.selectorRef}
      >
        <div
          className={classNames(
            `${BLOCK_NAME}__UI`,
            {
              [`${BLOCK_NAME}__UI--active`]: resultsVisible,
              [`${BLOCK_NAME}__UI--empty`]: !selected && !invalid,
              [`${BLOCK_NAME}__UI--invalid`]: !selected && invalid,
              [`${BLOCK_NAME}__UI--questions`]: questionsView
            }
          )}
          onClick={this.handleOnFocus}
          onKeyPress={this.handleEnter}
          role="button"
          tabIndex="0"
        >
          <ResponseItem
            placeholder={searchQuery || (hasCreateNewAnswerButton ? "Choose or Create an Answer" : "Choose an Answer")}
            response={selected}
            tags={tags}
            handleResponseClear={this.handleAnswerClear}
            questionsView={questionsView}
          />
          <SvgIcon
            icon="ChevronDown"
            customClassName={classNames(
              `${BLOCK_NAME}__UI__chevron`,
              {
                [`${BLOCK_NAME}__UI__chevron--questions`]: questionsView
              }
            )}
          />
        </div>

        {resultsVisible && (
          <div
            className={classNames(
              `${BLOCK_NAME}`,
              {
                [customClassName]: customClassName,
                [`${BLOCK_NAME}--disabled`]: disabled,
                [`${BLOCK_NAME}__modal--align-bottom`]: bottomAligned && !fixed
              }
            )}
          >
            {inModal ? this.renderContent("modal") : this.renderContent("app-container")}
          </div>
        )}
      </div>
    );
  }
}

ResponseSelector.propTypes = {
  searchQuery: PropTypes.string,
  updateSearchQuery: PropTypes.func.isRequired,
  clearAfterUpdate: PropTypes.bool,
  // Temporary until https://adasupport.atlassian.net/browse/BUIL-355
  questionsView: PropTypes.bool,
  disabled: PropTypes.bool,
  fixed: PropTypes.bool,
  inModal: PropTypes.bool,
  onClear: PropTypes.func,
  /* onDidUpdate(Response)  */
  onDidUpdate: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  preSelect: PropTypes.oneOfType([
    PropTypes.instanceOf(Response),
    PropTypes.instanceOf(ResponseRecord)
  ]),
  responses: ImmutablePropTypes.listOf(PropTypes.oneOfType([
    PropTypes.instanceOf(Response),
    PropTypes.instanceOf(ResponseRecord)
  ])).isRequired,
  filteredResponses: ImmutablePropTypes.listOf(PropTypes.oneOfType([
    PropTypes.instanceOf(Response),
    PropTypes.instanceOf(ResponseRecord)
  ])),
  onCreateNewAnswer: PropTypes.func,

  tags: ImmutablePropTypes.list.isRequired,
  responseFilterItems: ImmutablePropTypes.listOf(PropTypes.instanceOf(ResponseFilterItem)),
  hasCreateNewAnswerButton: PropTypes.bool,
  customClassName: PropTypes.string,
  fetchResponseFilterItems: PropTypes.func.isRequired
};

ResponseSelector.defaultProps = {
  searchQuery: null,
  /** Will not set a selected response internally after a response is selected */
  clearAfterUpdate: false,
  questionsView: false,
  disabled: false,
  fixed: false,
  onClear: null,
  inModal: false,
  placeholder: "",
  customClassName: "",
  preSelect: null,
  hasCreateNewAnswerButton: false,
  onCreateNewAnswer: null,
  responseFilterItems: null,
  filteredResponses: null
};

/**
 * @param {Object} state
 * @param {Object} ownProps
 * @param {String|null} ownProps.searchQuery
 * @param {Immutable.List.<ResponseRecord>} ownProps.responses
 * @returns {Object}
 */
function mapState(state, ownProps) {
  const { tags } = state;
  const { responses, searchQuery } = ownProps;
  const responseFilterItems = getResources(state, "responseFilterItem", {
    filter: { query: searchQuery }
  });

  return {
    tags,
    responseFilterItems,
    filteredResponses: responseFilterItems && (
      responses.filter(response => (
        responseFilterItems.some(responseFilterItem => (
          responseFilterItem.responseId === response.id
        ))
      ))
    )
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object.<Function>}
 */
function mapDispatchToProps(dispatch) {
  return {
    fetchResponseFilterItems: query => dispatch(
      fetchMany("responseFilterItem", { filter: { query } })
    )
  };
}

const Connector = connect(mapState, mapDispatchToProps)(ResponseSelector);

/**
 * A container to store `searchQuery` and passing it as a prop
 * We need it to make `searchQuery` available in `mapStateToProps`
 *
 * @param {Object} props
 * @returns {ReactElement}
 */
export default function ResponseSelectorContainer(props) {
  const [searchQuery, updateSearchQuery] = useState(null);

  return (
    <Connector
      {...props}
      searchQuery={searchQuery}
      updateSearchQuery={updateSearchQuery}
    />
  );
}

ResponseSelectorContainer.propTypes = {
  customClassName: PropTypes.string,
  invalid: PropTypes.bool,
  clearAfterUpdate: PropTypes.bool,
  hasCreateNewAnswerButton: PropTypes.bool,
  onDidUpdate: PropTypes.func.isRequired,
  preSelect: PropTypes.oneOfType([
    PropTypes.instanceOf(Response),
    PropTypes.instanceOf(ResponseRecord)
  ]),
  responses: ImmutablePropTypes.listOf(PropTypes.oneOfType([
    PropTypes.instanceOf(Response),
    PropTypes.instanceOf(ResponseRecord)
  ])).isRequired,
  onCreateNewAnswer: PropTypes.func
};

ResponseSelectorContainer.defaultProps = {
  customClassName: null,
  invalid: false,
  clearAfterUpdate: false,
  hasCreateNewAnswerButton: false,
  preSelect: null,
  onCreateNewAnswer: null
};

export { ResponseSelector };
